(() => {
  const Gift = window.SnowcubesGift || {};
  const GIFT_MODAL_LOAD_ERROR = "We couldn't open the gift-note options. Please try again.";
  const trackAnalytics = (eventName, properties) => {
    if (!eventName || typeof window.SnowcubesAnalytics?.track !== "function") return;

    try {
      window.SnowcubesAnalytics.track(eventName, properties);
    } catch (_) {
      // Analytics must never change the cart path.
    }
  };

  const trackProductAnalytics = (eventName, productData) => {
    if (!eventName || typeof window.SnowcubesAnalytics?.trackProduct !== "function") return;

    try {
      window.SnowcubesAnalytics.trackProduct(eventName, productData);
    } catch (_) {
      // Analytics must never change the cart path.
    }
  };

  // `variant_picker.liquid` stamps every picker with data-section/data-product-id,
  // `gift-box-customizer-button.liquid` stamps every trigger with
  // data-section-id/data-product-id, and `product-form.liquid` names the form
  // `Product-Form-{section}{product}`. variant-selects.js already resolves the
  // relation in that direction (`updateVariantInput`, `resolveOwningProductForm`);
  // this is the same relation read from the form's side, so a section holding
  // nine quick-add cards can never let one card answer for another.
  const ownerFormIdOf = (el) => {
    const sectionKey = el?.dataset.section || el?.dataset.sectionId;
    const productKey = el?.dataset.productId;
    return sectionKey && productKey ? `Product-Form-${sectionKey}${productKey}` : null;
  };
  const ownsForm = (el, formId) => !!formId && ownerFormIdOf(el) === formId;
  // An element that names its owner and does not name THIS form belongs to a
  // neighbouring card. It is never adoptable, not even when it is the only one
  // on the page — silently adopting it is the whole defect.
  const isUnclaimed = (el) => ownerFormIdOf(el) === null;

  // Resolve one element of `selector` for THIS form: the one it owns, else the
  // single ownerless candidate that older single-product markup renders.
  // Never "the first of many" — that is the bug.
  const resolveOwnedElement = (scope, selector, formId) => {
    const candidates = Array.from(scope.querySelectorAll(selector));
    const owned = candidates.find((el) => ownsForm(el, formId));
    if (owned) return owned;
    const unclaimed = candidates.filter(isUnclaimed);
    return unclaimed.length === 1 ? unclaimed[0] : null;
  };

  // The variant that is actually being added is the id in the form, not the
  // picker's transient `currentVariant` — which is undefined on any card the
  // buyer never opened. Prefer the picker's embedded variant JSON so the gift
  // classification describes the item Shopify will charge for.
  const variantTitleForSubmittedId = (picker, submittedVariantId) => {
    if (!picker || !submittedVariantId) return "";
    const current = picker.currentVariant;
    if (current && String(current.id) === submittedVariantId) return current.title || "";
    try {
      const variants = picker.getVariantData?.();
      const match = Array.isArray(variants)
        ? variants.find((variant) => String(variant?.id) === submittedVariantId)
        : null;
      if (match) return match.title || "";
    } catch (_) {
      // A picker that cannot read its own variant JSON falls through below.
    }
    return current?.title || "";
  };

  const readFlavorSelection = (formData) => {
    const raw = formData.get("properties[Flavor]");
    if (typeof raw !== "string") return [];

    return raw
      .split(",")
      .map((flavor) =>
        flavor.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, 40)
      )
      .filter(Boolean)
      .slice(0, 4);
  };

  if (!customElements.get("product-form")) {
    customElements.define(
      "product-form",
      class ProductForm extends HTMLElement {
        constructor() {
          super();

        this.form = this.querySelector("form");
        this.submitButton = this.querySelector('[type="submit"]');

        // Some contexts (e.g. placeholder quick-add content) render <product-form>
        // without a real <form>. Avoid crashing on upgrade.
        if (!this.form || !this.submitButton) return;

        const idInput = this.form.querySelector("[name=id]");
        if (idInput) idInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        // Passing no argument will hide the error message if one was on screen because errorText defaults to false
        this.handleErrorMessage();

        const section =
          this.closest("section, .product, [data-section]") || document;
        const submittedVariantValue = this.form.querySelector("[name=id]")?.value || "";
        // A Shopify add form necessarily contains `<input name="id">`. In
        // browsers, that named control can shadow HTMLFormElement#id, so
        // `this.form.id` may be the input element rather than the form's ID.
        // Read the attribute directly before resolving this card's picker.
        const formId = this.form.getAttribute("id") || "";
        // A section can contain several quick-add cards. Own the picker this
        // form is paired with, rather than letting the first picker make
        // another card require a gift note or corrupt telemetry. Matching on
        // the picker's live variant id is kept as a fallback for the single
        // product surfaces that render no owning id.
        const pickers = Array.from(
          section.querySelectorAll("variant-selects, variant-radios")
        );
        const variantSelect =
          resolveOwnedElement(section, "variant-selects, variant-radios", formId) ||
          pickers.find(
            (picker) =>
              isUnclaimed(picker) &&
              String(picker.currentVariant?.id || "") === submittedVariantValue
          ) ||
          undefined;
        const variantTitle = variantTitleForSubmittedId(variantSelect, submittedVariantValue);
        const isGiftWrapped = Gift.isGiftWrappedVariantTitle
          ? Gift.isGiftWrappedVariantTitle(variantTitle)
          : false;
        // Same ownership rule for the gift-note trigger: its `always on` flag
        // decides whether THIS card must collect a note, and it is the element
        // the modal is anchored to. One card's trigger must never speak for
        // its neighbours.
        //
        // This replaces an ancestor walk that ended in
        // `section.querySelector("[data-gift-note-card]")`. The walk handles a
        // card that wraps its own trigger, but a card that has NO trigger falls
        // through to that last line and is handed the first card's — including
        // its data-gift-always-on flag, which then demands a gift note on a
        // product with no gift packaging and no charge. Resolving by owner has
        // no such fallback: unowned means unowned.
        const giftNoteCard = resolveOwnedElement(section, "[data-gift-note-card]", formId);
        const giftAlwaysOn = !!giftNoteCard?.hasAttribute("data-gift-always-on");
        let hasSelectableGiftVariant = false;
        try {
          hasSelectableGiftVariant = !!variantSelect?.findGiftWrappedVariant?.();
        } catch (_) {
          hasSelectableGiftVariant = false;
        }
        const requiresGiftConfig = isGiftWrapped || (giftAlwaysOn && !hasSelectableGiftVariant);
        const giftConfigInput = this.form.querySelector('input[name="properties[_GiftCardConfig]"]');
        let hasGiftConfig = !!giftConfigInput?.value;

        Gift.debug?.("Product form submit", {
          variantTitle,
          isGiftWrapped,
          giftAlwaysOn,
          hasSelectableGiftVariant,
          requiresGiftConfig,
          hasGiftConfig,
        });

        // Gift config interception: If the product requires gift config (either via
        // Gift Wrapped variant OR giftAlwaysOn), ensure we have a config before
        // allowing add-to-cart. This prevents items that need a gift card from
        // reaching checkout without one.
        if (requiresGiftConfig && !hasGiftConfig) {
          // Preserve the customer's add intent. Completing the required gift
          // configuration resumes this exact form once; the second submit sees
          // the injected config and proceeds normally.
          const openRequiredGiftModal = () => {
            if (typeof window.openGiftBoxModal !== "function") {
              throw new Error("Gift modal is unavailable");
            }
            Gift.debug?.("Gift config required but missing; opening modal", {
              isGiftWrapped,
              giftAlwaysOn,
            });
            window.openGiftBoxModal({
              mode: "add",
              productForm: this.form,
              submitter: this.submitButton,
              trigger: giftNoteCard || null,
              resumeAddToCart: true,
              entryAction: "add_to_cart",
            });
          };
          const modalLoader = window.SnowcubesGiftModal;

          if (modalLoader?.ensureLoaded && typeof window.openGiftBoxModal !== "function") {
            this.submitButton.setAttribute("aria-disabled", true);
            this.submitButton.setAttribute("aria-busy", "true");
            this.submitButton.classList.add("loading");

            modalLoader.ensureLoaded()
              .then(() => {
                this.submitButton.removeAttribute("aria-disabled");
                this.submitButton.removeAttribute("aria-busy");
                this.submitButton.classList.remove("loading");
                if (this.form.isConnected) openRequiredGiftModal();
              })
              .catch(() => {
                this.submitButton.removeAttribute("aria-disabled");
                this.submitButton.removeAttribute("aria-busy");
                this.submitButton.classList.remove("loading");
                this.handleErrorMessage(
                  GIFT_MODAL_LOAD_ERROR
                );
                trackAnalytics(
                  window.SnowcubesAnalytics?.EVENTS?.GIFT_CUSTOMIZER_FAILED,
                  { gift_mode: "add", failure_class: "modal_load" }
                );
              });
          } else if (typeof window.openGiftBoxModal === "function") {
            try {
              openRequiredGiftModal();
            } catch (_) {
              this.handleErrorMessage(
                GIFT_MODAL_LOAD_ERROR
              );
              trackAnalytics(
                window.SnowcubesAnalytics?.EVENTS?.GIFT_CUSTOMIZER_FAILED,
                { gift_mode: "add", failure_class: "modal_open" }
              );
            }
          } else {
            this.handleErrorMessage(
              GIFT_MODAL_LOAD_ERROR
            );
          }
          return;
        }

        // Disabling the button temporarily so that the request can be processed
        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");

        const config = {
          method: "POST",
          headers: {
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
        };

        const formData = new FormData(this.form);
        const submittedVariantId = Number(formData.get("id")) || null;
        const selectedFlavors = readFlavorSelection(formData);
        formData.append("sections", ["cart-bubble", "cart-drawer"]);

        config.body = formData;

        const t0 = typeof performance !== "undefined" ? performance.now() : 0;
        let requestLatencyMs = null;
        const measureRequestLatency = () => {
          if (requestLatencyMs !== null) return requestLatencyMs;
          if (typeof performance === "undefined") return null;
          requestLatencyMs = Math.max(
            0,
            Math.min(120000, Math.round(performance.now() - t0))
          );
          return requestLatencyMs;
        };

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const dt = measureRequestLatency();
            Gift.debug?.("Cart add response received", {
              ok: response.ok,
              status: response.status,
              contentType: response.headers?.get?.("content-type") || "",
              ms: dt,
            });

            if (!response.ok) {
              const bodyText = await response.text().catch(() => "");
              const cartError = new Error(
                `Cart add failed (${response.status}): ${bodyText.slice(0, 200)}`
              );
              cartError.cartStatus = response.status;
              throw cartError;
            }

            return response.json();
          })
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);

              this.error = true;
              trackAnalytics(
                window.SnowcubesAnalytics?.EVENTS?.ADD_TO_CART_FAILED,
                {
                  reason: "shopify_rejection",
                  status: response.status,
                  latency_ms: measureRequestLatency(),
                  variant_id: submittedVariantId,
                  ...(selectedFlavors.length
                    ? {
                        selected_flavors: selectedFlavors,
                        flavor_selection_count: selectedFlavors.length,
                      }
                    : {}),
                }
              );

              return;
            }

            // `sections` is absent whenever Shopify answers /cart/add without
            // section rendering, so it must be guarded like the drawer markup
            // below. Reading through it unguarded threw
            // "Cannot read properties of null (reading 'cart-bubble')" AFTER a
            // successful HTTP 200 add, which dropped the whole remainder of the
            // success path: the cart drawer never opened and, worse, the throw
            // landed in .catch() and reported a completed add as
            // add_to_cart_failed reason=network_error. A real purchase step was
            // being recorded as a failure.
            const cartBubbleCount = response.sections?.["cart-bubble"]?.match(/\d+/);

            if (cartBubbleCount) {
              const cartBubbleOne = document.getElementById("cart-details--sticky");
              if (cartBubbleOne) cartBubbleOne.innerHTML = `${cartBubbleCount}`;

              const cartBubbleTwo = document.getElementById("cart-details--relative");
              if (cartBubbleTwo) cartBubbleTwo.innerHTML = `${cartBubbleCount}`;
            }

            const drawerHtml = response.sections?.["cart-drawer"];

            const dialog1 = document.getElementById("dialog-cart-1");
            const dialog1Body = dialog1?.querySelector(".dialog__content__body");
            if (dialog1Body && drawerHtml) dialog1Body.innerHTML = `${drawerHtml}`;

            const dialog2 = document.getElementById("dialog-cart-2");
            const dialog2Body = dialog2?.querySelector(".dialog__content__body");
            if (dialog2Body && drawerHtml) dialog2Body.innerHTML = `${drawerHtml}`;

            if (window.SnowcubesDeliveryCalendar?.scan) {
              window.SnowcubesDeliveryCalendar.scan(dialog1Body || dialog2Body || document);
            }

            if (dialog1?.parentNode) dialog1.parentNode.open = true;
            if (dialog2?.parentNode) dialog2.parentNode.open = true;

            this.error = false;
            // Identity comes from the cart response (the item Shopify added),
            // never from a section-scoped picker: on multi-quick-add sections
            // the picker belongs to a different card unless it matches this form.
            const addedVariantTitle = response.variant_title ?? null;
            // Gift classification is part of the added item's identity too.
            // `isGiftWrapped` above controls this form's required-note flow;
            // it must not leak into analytics for a different quick-add card.
            const addedGiftWrapOption = Gift.isGiftWrappedVariantTitle
              ? Gift.isGiftWrappedVariantTitle(addedVariantTitle)
              : false;
            const quantityInput = this.form.querySelector('[name="quantity"]');
            trackProductAnalytics(
              window.SnowcubesAnalytics?.EVENTS?.ADD_TO_CART,
              {
                id: response.product_id ?? null,
                title: response.product_title ?? null,
                variantId: response.id ?? submittedVariantId,
                variantTitle: addedVariantTitle,
                price: response.price != null ? response.price / 100 : null,
                quantity:
                  response.quantity ??
                  (quantityInput ? Number(quantityInput.value) || 1 : 1),
                latencyMs: measureRequestLatency(),
                selectedFlavors,
                isGiftWrapOption: addedGiftWrapOption,
              }
            );

            // A confirmed note belongs to this one add. Reusing the form or
            // returning to the PDP must require a fresh confirmation.
            ["_GiftCardConfig", "_GiftCardTo", "_GiftCardFrom", "_GiftCardMessage"].forEach((key) => {
              this.form.querySelector(`input[name="properties[${key}]"]`)?.remove();
            });
            Gift.giftConfigPrototype?.clear?.(this.dataset.productHandle || null);
          })
          .catch((error) => {
            console.error(error);
            trackAnalytics(
              window.SnowcubesAnalytics?.EVENTS?.ADD_TO_CART_FAILED,
              {
                reason: error?.cartStatus ? "http_error" : "network_error",
                status: error?.cartStatus || null,
                latency_ms: measureRequestLatency(),
                variant_id: submittedVariantId,
                ...(selectedFlavors.length
                  ? {
                      selected_flavors: selectedFlavors,
                      flavor_selection_count: selectedFlavors.length,
                    }
                  : {}),
              }
            );
            // Don't fail silently — surface a message so the user isn't left with a
            // spinner that resets, no item added, and no idea why it didn't work.
            this.handleErrorMessage(
              "We couldn't add that to your cart. Please try again."
            );
            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            this.submitButton.removeAttribute("aria-disabled");
          });
      }

      handleErrorMessage(errorText = false) {
        if (this.hideErrors) return;

        this.errorMessage =
          this.errorMessage || this.querySelector(".message-error");
        if (!this.errorMessage) return;

        const svg = this.errorMessage.querySelector("svg");
        let span = this.errorMessage.querySelector("span");
        const hasError =
          typeof errorText === "string"
            ? errorText.trim().length > 0
            : Boolean(errorText);

        if (hasError) {
          if (!span) {
            span = document.createElement("span");
            if (svg) {
              svg.after(span);
            } else {
              this.errorMessage.appendChild(span);
            }
          }
          span.textContent = errorText;
          this.errorMessage.classList.remove("hidden");
          this.errorMessage.hidden = false;
        } else if (span) {
          span.remove();
          this.errorMessage.classList.add("hidden");
          this.errorMessage.hidden = true;
        } else {
          this.errorMessage.classList.add("hidden");
          this.errorMessage.hidden = true;
        }
      }
      }
    );
  }
})();
